import React from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowRight, Clock, ListChecks } from 'lucide-react';

/**
 * MatchMeCTA - prominent "Match me with builders" strip.
 *
 * Purpose:
 *   Surfaces the builder-matcher questionnaire in a high-visibility band on
 *   category and detail pages so lower-funnel visitors get a clear next step
 *   without having to hunt through the workspace menu.
 *
 * Usage:
 *   <MatchMeCTA context="land" listing={listing} />
 *   <MatchMeCTA context="package" listing={listing} />
 *   <MatchMeCTA context="hub" />
 *
 * Design:
 *   Dark surface with a brand-blue accent chip. 3 mini-tiles of proof (time,
 *   scope, output) sit under the primary CTA to reduce friction. Reuses the
 *   existing /workspace/tools/find-a-builder questionnaire route with
 *   ?skip-matcher=1 so the wizard opens on the first question.
 */
const MatchMeCTA = ({ context = 'hub', listing = null, className = '' }) => {
  const suburb = listing?.suburb || listing?.suburb_name || null;
  const state = listing?.state || 'SA';

  const eyebrow = 'Get matched with builders';
  const builderRegion = state === 'VIC' ? 'Victorian' : 'Adelaide';
  const title = context === 'land'
    ? (suburb ? `Which builders can build on this ${suburb} block?` : 'Which builders can build on this block?')
    : context === 'package'
      ? 'Compare partner builders that offer packages like this'
      : context === 'suburb'
        ? (suburb ? `Which builders should you talk to in ${suburb}?` : 'Which builders should you talk to in this suburb?')
        : context === 'compare'
          ? 'Ready to compare against your own brief?'
          : 'Get matched with the right builder for your build';
  const body = context === 'suburb'
    ? `A 3-minute questionnaire on budget, block type and build style. We shortlist ${builderRegion} partner builders that actively service this suburb, and hand you a comparison table. No pushy calls, no commitment.`
    : context === 'compare'
      ? 'Comparison tables help you understand the market. In 3 minutes we\u2019ll narrow the market down to the 3 builders best matched to YOUR budget, block and build style.'
      : `A 3-minute questionnaire on budget, block type and build style. We shortlist ${builderRegion} partner builders that fit, and hand you a comparison table. No pushy calls, no commitment.`;

  // Deep-link the wizard with any context we have so the questionnaire
  // pre-fills where possible.
  const params = new URLSearchParams();
  params.set('skip-matcher', '1');
  if (suburb) params.set('suburb', suburb);
  if (state) params.set('state', state);
  const to = `/workspace/tools/find-a-builder?${params.toString()}`;

  return (
    <section
      data-testid={`match-me-cta-${context}`}
      aria-label="Get matched with builders"
      className={`my-6 rounded-2xl bg-gradient-to-br from-[#0a2540] via-[#1d3a5f] to-[#1d1d1f] p-6 sm:p-8 text-white ${className}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 items-center">
        <div className="min-w-0">
          <h3 className="text-[22px] sm:text-[26px] font-bold leading-tight tracking-tight mb-3">
            {title}
          </h3>
          <p className="text-[14px] text-white/75 leading-[1.6] max-w-2xl mb-5">
            {body}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={to}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[14px] font-semibold text-[#0a2540] hover:bg-[#4abff2] hover:text-white transition-colors"
              data-testid={`match-me-cta-${context}-primary`}
            >
              Match me with builders <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/one-on-one-support"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3 text-[14px] font-medium text-white hover:border-white/60 hover:bg-white/10 transition-colors"
              data-testid={`match-me-cta-${context}-secondary`}
            >
              Or speak to a human
            </Link>
          </div>
        </div>
        <ul className="space-y-2.5 lg:pl-6 lg:border-l lg:border-white/10">
          <li className="flex items-start gap-2.5 text-[13px] text-white/85">
            <Clock className="w-4 h-4 mt-0.5 text-[#4abff2]" />
            <span><strong className="text-white">Under 3 minutes.</strong> 8 quick questions, no email required to see results.</span>
          </li>
          <li className="flex items-start gap-2.5 text-[13px] text-white/85">
            <ListChecks className="w-4 h-4 mt-0.5 text-[#4abff2]" />
            <span><strong className="text-white">Ranked shortlist.</strong> Match score, price band, and where each builder actually builds.</span>
          </li>
          <li className="flex items-start gap-2.5 text-[13px] text-white/85">
            <Users className="w-4 h-4 mt-0.5 text-[#4abff2]" />
            <span><strong className="text-white">Independent.</strong> Editorial layer over paid ranking. Builders can&apos;t buy their way to the top.</span>
          </li>
        </ul>
      </div>
    </section>
  );
};

export default MatchMeCTA;
